import { gql, useMutation, useQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Fab,
  FormHelperText,
  Grid2 as Grid,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Modal from 'components/Modal'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useGetMe } from 'components/Session/hooks'
import { PreQualifiedSite, Company } from 'utils/__generated__/graphql'
import * as Yup from 'yup'

import { TIMELINE_BASE_QUERIES, TIMELINE_FRAGMENT } from '../constants'

const timelineSchema = Yup.object({
  site: Yup.string().required('Please select a Site'),
  name: Yup.string().required('Please enter a name'),
})

export type TimelineDataType = Yup.InferType<typeof timelineSchema>

type TimelineFormProps = {
  sites: PreQualifiedSite[]
  companies: Company[]
  values?: TimelineDataType
  onTimeline: (data: TimelineDataType) => void
}

export function TimelineForm({ sites, onTimeline, values }: TimelineFormProps) {
  const { control, handleSubmit } = useForm({
    defaultValues: values || {
      site: sites[0]?.id || '',
      name: '',
    },
    resolver: yupResolver(timelineSchema),
  })

  return (
    <Box component="form" onSubmit={handleSubmit(onTimeline)}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="name">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Name*
                  </Typography>
                </InputLabel>
                <TextField
                  id="name"
                  error={!!error}
                  onChange={onChange}
                  value={value}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={error && error.message}
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            name="site"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const sortedSites = [...sites].sort((a, b) =>
                (a.name || '').localeCompare(b.name || '')
              )
              const selectedSite = sortedSites.find(s => s.id === value) || null

              return (
                <React.Fragment>
                  <InputLabel>
                    <Typography sx={theme => ({ ...theme.typography.caption })}>
                      Site*
                    </Typography>
                  </InputLabel>
                  <Autocomplete
                    size="small"
                    options={sortedSites}
                    getOptionLabel={option => option.name || ''}
                    value={selectedSite}
                    onChange={(_, selected) => onChange(selected?.id || '')}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!error}
                        placeholder="Search sites..."
                      />
                    )}
                  />
                  {error && (
                    <FormHelperText error>{error.message}</FormHelperText>
                  )}
                </React.Fragment>
              )
            }}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 1, mb: 1 }} />
      <Button
        type="submit"
        sx={{ float: 'right' }}
        variant="contained"
        disableElevation
      >
        Save
      </Button>
    </Box>
  )
}

const CREATE_TIMELINE = gql`
  ${TIMELINE_FRAGMENT}
  mutation CREATE_TIMELINE($timeline: TimelineInput!) {
    createTimeline(timeline: $timeline) {
      ...TimelineFragment
    }
  }
`

function Create() {
  const [open, setOpen] = useState<boolean>(false)
  const { data: meData } = useGetMe()

  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [createTimeline] = useMutation(CREATE_TIMELINE)

  const handleTimeline = (data: TimelineDataType) => {
    createTimeline({
      variables: {
        timeline: {
          user: meData?.me?.id,
          company: meData?.me?.company?.id,
          isAdminTimeline: true,
          ...data,
        },
      },
      onCompleted: handleClose,
      update(cache, { data: { createTimeline } }) {
        cache.modify({
          fields: {
            findTimelines(existingTimelines = []) {
              const newTimelineRef = cache.writeFragment({
                data: createTimeline,
                fragment: gql`
                  fragment NewTimeline on Timeline {
                    id
                  }
                `,
              })
              return [newTimelineRef, ...existingTimelines]
            },
          },
        })
      },
    })
  }

  const { loading, error, data } = useQuery(TIMELINE_BASE_QUERIES, {
    variables: { siteFilter: { isPublished: true }, companyFilter: {} },
    skip: !open,
  })

  const sites = data?.sites || []
  const companies = data?.companies || []

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={480}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Create Timeline
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        {loading ? (
          <Stack sx={{ alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <p>error...</p>
        ) : (
          <TimelineForm
            onTimeline={handleTimeline}
            sites={sites}
            companies={companies}
          />
        )}
      </Modal>
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleOpen}
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
      >
        <AddIcon />
      </Fab>
    </React.Fragment>
  )
}

export default Create
